'use client'

/**
 * Tournament invites UI (design: docs/tournament-invites-plan.md §5).
 *
 * - `InvitesSection` — the organizer's manage-page card: header + "Invite
 *   guests" button, then the sent list with status pills, or an empty card.
 * - `InviteGuestsModal` — Luma-style two-pane picker: sources on the left
 *   (your matches / all past guests / per past event), searchable person list
 *   on the right, then a compose step with an optional message.
 * - `InviteBanner` — shown on the public event page when the viewer has a
 *   pending invite; swaps the Join CTA for Accept / Decline.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Mail,
  Plus,
  Search,
  Sparkles,
  Users,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/tournaments'
import { useLocalTimezone } from '@/lib/use-local-timezone'
import { UserAvatar } from '@/components/ui/user-avatar'
import { ListPager } from './shared'
import {
  cancelInvite,
  getInviteCandidates,
  getInvitesForOrganizer,
  getInviteSources,
  getMyInvites,
  respondToInvite,
  sendInvites,
  type InviteCandidate,
  type InviteSourceKey,
  type InviteSources,
  type MyInvite,
  type SentInvite,
} from '@/lib/tournament-invites'

/** Per-request cap enforced by send_tournament_invites. */
const MAX_PER_SEND = 50

/** Sent-invites list page size. */
const INVITES_PER_PAGE = 5

// ---------------------------------------------------------------------------
// Organizer manage-page section
// ---------------------------------------------------------------------------

export function InvitesSection({ tournamentId }: { tournamentId: string }) {
  const [invites, setInvites] = useState<SentInvite[] | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [page, setPage] = useState(0)

  const reload = useCallback(() => {
    getInvitesForOrganizer(tournamentId).then(setInvites)
  }, [tournamentId])

  // Clamp so a shrinking list can't strand the page out of range.
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil((invites?.length ?? 0) / INVITES_PER_PAGE) - 1)
  )

  useEffect(() => {
    reload()
  }, [reload])

  const onCancel = async (inviteId: string) => {
    if (await cancelInvite(inviteId)) reload()
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Invites</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Invite your matches and past guests to this event.
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-primary px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 cursor-pointer"
        >
          <Plus className="size-4" /> Invite guests
        </button>
      </div>

      <div className="mt-5">
        {invites === null ? (
          <div className="h-16 animate-pulse rounded-xl bg-white/5" />
        ) : invites.length === 0 ? (
          <div className="flex items-center gap-4 rounded-xl border border-dashed border-border bg-card/40 px-5 py-6">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Mail className="size-5" />
            </div>
            <div>
              <p className="text-sm font-semibold">No invites sent</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                You can invite your matches and past event guests.
              </p>
            </div>
          </div>
        ) : (
          <>
          <ul className="divide-y divide-white/5">
            {invites
              .slice(safePage * INVITES_PER_PAGE, (safePage + 1) * INVITES_PER_PAGE)
              .map((inv) => (
              <li key={inv.inviteId} className="flex items-center gap-3 py-2.5">
                <UserAvatar url={inv.avatar} alt={inv.gamertag} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{inv.gamertag}</p>
                  <p className="text-xs text-muted-foreground">
                    Invited {new Date(inv.invitedAt).toLocaleDateString()}
                  </p>
                </div>
                <StatusPill status={inv.status} />
                {inv.status === 'pending' && (
                  <button
                    onClick={() => onCancel(inv.inviteId)}
                    aria-label={`Cancel invite to ${inv.gamertag}`}
                    className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground cursor-pointer"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <ListPager
            page={safePage}
            pageSize={INVITES_PER_PAGE}
            total={invites.length}
            onPageChange={setPage}
          />
          </>
        )}
      </div>

      {modalOpen && (
        <InviteGuestsModal
          tournamentId={tournamentId}
          onClose={() => setModalOpen(false)}
          onSent={() => {
            setModalOpen(false)
            reload()
          }}
        />
      )}
    </section>
  )
}

function StatusPill({ status }: { status: SentInvite['status'] }) {
  const meta: Record<SentInvite['status'], { label: string; cls: string }> = {
    pending: { label: 'Pending', cls: 'bg-white/10 text-white/70' },
    accepted: { label: 'Accepted', cls: 'bg-emerald-500/15 text-emerald-300' },
    declined: { label: 'Declined', cls: 'bg-white/5 text-muted-foreground' },
    cancelled: { label: 'Cancelled', cls: 'bg-white/5 text-muted-foreground line-through' },
  }
  const m = meta[status]
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', m.cls)}>
      {m.label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Invite Guests modal (two-pane, Luma-style)
// ---------------------------------------------------------------------------

type ActiveSource =
  | { key: 'matches' }
  | { key: 'past_guests' }
  | { key: 'event'; eventId: string }

function InviteGuestsModal({
  tournamentId,
  onClose,
  onSent,
}: {
  tournamentId: string
  onClose: () => void
  onSent: () => void
}) {
  const localTz = useLocalTimezone()
  const [sources, setSources] = useState<InviteSources | null>(null)
  const [active, setActive] = useState<ActiveSource>({ key: 'matches' })
  const [query, setQuery] = useState('')
  const [candidates, setCandidates] = useState<InviteCandidate[] | null>(null)
  const [selected, setSelected] = useState<Map<string, InviteCandidate>>(new Map())
  const [step, setStep] = useState<'pick' | 'compose'>('pick')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Serial per candidate fetch; a stale response must not clobber a newer one.
  const fetchSeq = useRef(0)

  useEffect(() => {
    getInviteSources(tournamentId).then(setSources)
  }, [tournamentId])

  // Load candidates whenever the source or (debounced) query changes.
  useEffect(() => {
    const seq = ++fetchSeq.current
    setCandidates(null)
    const t = setTimeout(async () => {
      const list = await getInviteCandidates(
        tournamentId,
        active.key as InviteSourceKey,
        {
          eventId: active.key === 'event' ? active.eventId : undefined,
          query: query || undefined,
          limit: 50,
        }
      )
      if (seq === fetchSeq.current) setCandidates(list)
    }, query ? 250 : 0)
    return () => clearTimeout(t)
  }, [tournamentId, active, query])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const toggle = (c: InviteCandidate) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(c.userId)) next.delete(c.userId)
      else if (next.size < MAX_PER_SEND) next.set(c.userId, c)
      return next
    })
  }

  const send = async () => {
    if (selected.size === 0 || sending) return
    setSending(true)
    setError(null)
    const res = await sendInvites(tournamentId, [...selected.keys()], message)
    setSending(false)
    if ('error' in res) {
      setError(res.error)
      return
    }
    onSent()
  }

  const sourceLabel = useMemo(() => {
    if (active.key === 'matches') return 'your matches'
    if (active.key === 'past_guests') return 'past guests'
    return sources?.events.find((e) => e.id === active.eventId)?.title ?? 'event guests'
  }, [active, sources])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Invite guests"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-card shadow-2xl shadow-black/50"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            {step === 'compose' && (
              <button
                onClick={() => setStep('pick')}
                aria-label="Back to selection"
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground cursor-pointer"
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            <h3 className="text-base font-semibold">Invite guests</h3>
          </div>
          <div className="flex items-center gap-2.5">
            {/* Luma-style remaining-quota pill: rate limits should be legible
                up front, not a send-time surprise. */}
            <span className="rounded-full border border-white/10 px-2.5 py-1 text-xs font-medium tabular-nums text-muted-foreground">
              {MAX_PER_SEND - selected.size} left
            </span>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground cursor-pointer"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {step === 'pick' ? (
          <div className="flex min-h-0 flex-1">
            {/* Left: sources */}
            <div className="w-44 shrink-0 overflow-y-auto border-r border-white/10 p-2 sm:w-56">
              <SourceButton
                icon={<Sparkles className="size-4" />}
                label="Your matches"
                count={sources?.matches}
                active={active.key === 'matches'}
                onClick={() => setActive({ key: 'matches' })}
              />
              <SourceButton
                icon={<Users className="size-4" />}
                label="All past guests"
                count={sources?.pastGuests}
                active={active.key === 'past_guests'}
                onClick={() => setActive({ key: 'past_guests' })}
              />
              {sources !== null && sources.events.length > 0 && (
                <>
                  <p className="px-3 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground/70">
                    Events
                  </p>
                  {sources.events.map((e) => (
                    <button
                      key={e.id}
                      onClick={() => setActive({ key: 'event', eventId: e.id })}
                      className={cn(
                        'w-full rounded-xl px-3 py-2 text-left transition-colors cursor-pointer',
                        active.key === 'event' && active.eventId === e.id
                          ? 'bg-white/10'
                          : 'hover:bg-white/5'
                      )}
                    >
                      <span className="block truncate text-xs font-medium">{e.title}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {formatDate(e.startsAt, undefined, localTz)}{' '}
                        · {e.guests} guest{e.guests === 1 ? '' : 's'}
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>

            {/* Right: search + people */}
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="p-3">
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <Search className="size-4 shrink-0 text-muted-foreground" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={`Search ${sourceLabel}`}
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
                  />
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                {candidates === null ? (
                  <div className="space-y-2 p-2">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="h-11 animate-pulse rounded-xl bg-white/5" />
                    ))}
                  </div>
                ) : candidates.length === 0 ? (
                  <p className="px-3 py-10 text-center text-sm text-muted-foreground">
                    {query
                      ? `No one matching “${query}”.`
                      : 'No one left to invite from this list.'}
                  </p>
                ) : (
                  candidates.map((c) => {
                    const isSelected = selected.has(c.userId)
                    return (
                      <button
                        key={c.userId}
                        onClick={() => toggle(c)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors cursor-pointer',
                          isSelected ? 'bg-primary/10' : 'hover:bg-white/5'
                        )}
                      >
                        <UserAvatar url={c.avatar} alt={c.gamertag} size={32} />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {c.gamertag}
                        </span>
                        <span
                          className={cn(
                            'flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                            isSelected
                              ? 'border-primary bg-primary text-white'
                              : 'border-white/20'
                          )}
                        >
                          {isSelected && <Check className="size-3" />}
                        </span>
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Compose step */
          <div className="flex min-h-0 flex-1 flex-col p-5">
            <div className="flex flex-wrap gap-1.5">
              {[...selected.values()].map((c) => (
                <span
                  key={c.userId}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/5 py-1 pl-1 pr-2.5 text-xs font-medium"
                >
                  <UserAvatar url={c.avatar} alt={c.gamertag} size={18} />
                  {c.gamertag}
                </span>
              ))}
            </div>
            <label className="mt-4 block text-sm font-medium" htmlFor="invite-message">
              Add a message <span className="text-muted-foreground">(optional)</span>
            </label>
            <textarea
              id="invite-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 280))}
              rows={4}
              placeholder="Come play with us — spots are going fast!"
              className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-white/25"
            />
            <p className="mt-1 text-right text-xs tabular-nums text-muted-foreground">
              {message.length}/280
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-white/10 px-5 py-3.5">
          <span className="text-sm text-muted-foreground">
            {selected.size} selected
            {error && <span className="ml-2 text-red-400">{error}</span>}
          </span>
          {step === 'pick' ? (
            <button
              onClick={() => setStep('compose')}
              disabled={selected.size === 0}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-40 cursor-pointer"
            >
              Next <ChevronRight className="size-4" />
            </button>
          ) : (
            <button
              onClick={send}
              disabled={sending || selected.size === 0}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-40 cursor-pointer"
            >
              {sending
                ? 'Sending…'
                : `Send ${selected.size} invite${selected.size === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function SourceButton({
  icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  count: number | undefined
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors cursor-pointer',
        active ? 'bg-white/10' : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'
      )}
    >
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      {count !== undefined && (
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground/70">{count}</span>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Invitee banner on the public event page
// ---------------------------------------------------------------------------

export function InviteBanner({
  tournamentId,
  signedIn,
  onResponded,
}: {
  tournamentId: string
  signedIn: boolean
  /** Called after accepting so the page can refresh registration standing. */
  onResponded?: () => void
}) {
  const [invite, setInvite] = useState<MyInvite | null>(null)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<'accepted' | 'declined' | null>(null)

  useEffect(() => {
    if (!signedIn) return
    let cancelled = false
    // my_tournament_invites is capped at 100 pending invites — filtering
    // client-side keeps the surface to one RPC.
    getMyInvites().then((list) => {
      if (!cancelled) {
        setInvite(list.find((i) => i.tournamentId === tournamentId) ?? null)
      }
    })
    return () => {
      cancelled = true
    }
  }, [signedIn, tournamentId])

  if (!invite || outcome === 'declined') return null

  const respond = async (accept: boolean) => {
    if (busy) return
    setBusy(true)
    const res = await respondToInvite(invite.inviteId, accept)
    setBusy(false)
    if (res.ok) {
      setOutcome(accept ? 'accepted' : 'declined')
      if (accept) onResponded?.()
    }
  }

  if (outcome === 'accepted') {
    return (
      <div className="mb-3 flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-5 py-3.5">
        <Check className="size-4 shrink-0 text-emerald-300" />
        <p className="text-sm text-emerald-200">
          Invite accepted — you&apos;re registered for this event.
        </p>
      </div>
    )
  }

  return (
    <div className="mb-3 flex flex-col gap-3 rounded-2xl border border-primary/25 bg-primary/10 px-5 py-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <UserAvatar url={invite.inviter.avatar} alt={invite.inviter.gamertag} size={34} />
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            @{invite.inviter.gamertag} invited you to this event
          </p>
          {invite.message && (
            <p className="mt-0.5 truncate text-xs italic text-muted-foreground">
              “{invite.message}”
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <button
          onClick={() => respond(true)}
          disabled={busy}
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
        >
          Accept invite
        </button>
        <button
          onClick={() => respond(false)}
          disabled={busy}
          className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium transition-colors hover:bg-white/10 disabled:opacity-50 cursor-pointer"
        >
          Decline
        </button>
      </div>
    </div>
  )
}
